import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../../environments/environment';

declare const OpenPay: any;

@Component({
  selector: 'app-realizar-pago',
  templateUrl: './realizar-pago.component.html',
  styleUrls: ['./realizar-pago.component.scss']
})
export class RealizarPagoComponent implements OnInit {
  paymentForm: FormGroup;
  datos: any = null;
  montoPago = 300;
  montoMinimo = 300;
  montoMaximo = 0;
  montoError = '';
  metodo = 'card';
  cargando = true;
  procesando = false;
  error = '';
  referenciaEfectivo = '';
  fichaEfectivo = '';
  comprobantePago: any = null;
  comprobanteArchivo: File | null = null;
  comprobanteMensaje = '';
  comprobanteError = '';
  subiendoComprobante = false;
  private deviceSessionId = '';

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private modalService: NgbModal
  ) {
    this.paymentForm = this.fb.group({
      holderName: ['', Validators.required],
      cardNumber: ['', [Validators.required, Validators.minLength(13)]],
      expirationMonth: ['', [Validators.required, Validators.minLength(2)]],
      expirationYear: ['', [Validators.required, Validators.minLength(2)]],
      cvv2: ['', [Validators.required, Validators.minLength(3)]]
    });
  }

  ngOnInit(): void {
    this.cargarPago();
    this.cargarComprobantePago();
  }

  private headers(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: localStorage.getItem('token') || ''
    });
  }

  private fileHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: localStorage.getItem('token') || ''
    });
  }

  cargarPago(): void {
    this.http.get<any>(environment.BASE_URL + '/api/pago/', {headers: this.headers()}).subscribe(
      response => {
        this.datos = response.content;
        this.montoMinimo = Number(this.datos.monto_minimo || 300);
        this.montoMaximo = Number(this.datos.monto_maximo || this.datos.total || this.montoMinimo);
        this.montoPago = this.normalizarMonto(this.datos.monto_pago || this.montoMinimo);
        this.validarMontoPago();
        this.paymentForm.patchValue({holderName: this.datos.nombre});
        this.configurarOpenpay();
        this.cargando = false;
      },
      () => {
        this.cargando = false;
        this.error = 'No fue posible cargar la información del pago.';
      }
    );
  }

  private configurarOpenpay(): void {
    if (typeof OpenPay === 'undefined') {
      this.error = 'Openpay no está disponible. Recarga la página e intenta nuevamente.';
      return;
    }
    OpenPay.setId(this.datos.openpay_merchant_id);
    OpenPay.setApiKey(this.datos.openpay_public_key);
    OpenPay.setSandboxMode(this.datos.openpay_sandbox === true || this.datos.openpay_sandbox === 'true');
    setTimeout(() => {
      this.deviceSessionId = OpenPay.deviceData.setup('openpay-payment-form', 'device-session-id');
      if (!this.deviceSessionId) {
        this.error = 'No fue posible iniciar la validación antifraude de Openpay. Recarga la página e intenta nuevamente.';
      }
    });
  }

  pagar(): void {
    this.error = '';
    if (!this.validarMontoPago()) {
      return;
    }
    if (this.metodo === 'store') {
      this.enviarCargo({method: 'store'});
      return;
    }
    if (this.paymentForm.invalid) {
      this.paymentForm.markAllAsTouched();
      return;
    }
    if (!this.deviceSessionId) {
      this.error = 'No fue posible obtener la sesión segura de Openpay. Recarga la página e intenta nuevamente.';
      return;
    }
    this.procesando = true;
    OpenPay.token.extractFormAndCreate(
      'openpay-payment-form',
      response => this.enviarCargo({
        method: 'card',
        token_id: response.data.id,
        device_session_id: this.deviceSessionId
      }),
      error => {
        this.procesando = false;
        this.error = this.mensajeErrorOpenpay(error);
      }
    );
  }

  private enviarCargo(payload: any): void {
    this.procesando = true;
    const routePrefix = this.paymentRoutePrefix();
    payload.redirect_url = window.location.origin + routePrefix + '/pago-confirmado';
    payload.amount = this.montoPago;
    this.http.post<any>(environment.BASE_URL + '/api/pago/', payload, {headers: this.headers()}).subscribe(
      response => {
        this.procesando = false;
        if (response.requires_3d_secure && response.redirect_url) {
          window.location.href = response.redirect_url;
          return;
        }
        if (response.reference) {
          this.referenciaEfectivo = response.reference;
          this.fichaEfectivo = routePrefix + '/ficha-pago-efectivo/' + encodeURIComponent(response.charge_id);
          return;
        }
        alert('Tu pago se ha efectuado correctamente.');
        this.router.navigate([routePrefix === '/cliente' ? '/cliente/seguimiento' : '/perfil/pagos']);
      },
      response => {
        this.procesando = false;
        const apiError = response.error || {};
        const message = apiError.error || 'No fue posible procesar el pago.';
        this.error = message;
      }
    );
  }

  private mensajeErrorOpenpay(error: any): string {
    const code = Number(error && error.data ? error.data.error_code : 0);
    const messages: {[key: number]: string} = {
      1000: 'Ocurrió un error interno en Openpay. Intenta nuevamente.',
      1001: 'Los datos de la tarjeta tienen un formato incorrecto.',
      1004: 'Completa todos los datos de la tarjeta.',
      2004: 'Los datos de esta tarjeta ya fueron utilizados.',
      3001: 'Tarjeta rechazada. Intente con otra tarjeta.',
      3002: 'Tarjeta rechazada. Intente con otra tarjeta.',
      3003: 'Tarjeta rechazada. Intente con otra tarjeta.',
      3004: 'Tarjeta rechazada. Intente con otra tarjeta.',
      3005: 'Tarjeta rechazada. Intente con otra tarjeta.',
      3006: 'Tarjeta rechazada. Intente con otra tarjeta.',
      3008: 'Tarjeta rechazada. Intente con otra tarjeta.',
      3009: 'Tarjeta rechazada. Intente con otra tarjeta.',
      3010: 'Tarjeta rechazada. Intente con otra tarjeta.',
      3011: 'Tarjeta rechazada. Intente con otra tarjeta.',
      3012: 'Tarjeta rechazada. Intente con otra tarjeta.'
    };
    if (messages[code]) {
      return messages[code];
    }
    return code
      ? 'Tarjeta rechazada. Intente con otra tarjeta.'
      : 'No se pudo completar la transacción.';
  }

  cerrarSesion(): void {
    localStorage.removeItem('token');
    this.router.navigate(['/']);
  }

  cargarComprobantePago(): void {
    this.http.get<any>(environment.BASE_URL + '/api/comprobantePago/', {headers: this.headers()}).subscribe(
      response => {
        if (response.success) {
          this.comprobantePago = response.content;
        }
      },
      () => {
        this.comprobanteError = 'No fue posible cargar tu comprobante.';
      }
    );
  }

  abrirComprobantePago(modal: any): void {
    this.comprobanteArchivo = null;
    this.comprobanteMensaje = '';
    this.comprobanteError = '';
    this.modalService.open(modal, {ariaLabelledBy: 'modal-comprobante-pago'});
  }

  onComprobantePagoChange(files: FileList): void {
    this.comprobanteArchivo = files && files.length ? files.item(0) : null;
    this.comprobanteMensaje = '';
    this.comprobanteError = '';
  }

  subirComprobantePago(): void {
    if (!this.comprobanteArchivo) {
      this.comprobanteError = 'Selecciona un archivo.';
      return;
    }

    const formData = new FormData();
    formData.append('archivo', this.comprobanteArchivo);
    this.subiendoComprobante = true;
    this.http.post<any>(environment.BASE_URL + '/api/comprobantePago/', formData, {headers: this.fileHeaders()}).subscribe(
      response => {
        this.subiendoComprobante = false;
        if (response.success) {
          this.comprobantePago = response.content;
          this.comprobanteArchivo = null;
          this.comprobanteMensaje = 'Comprobante guardado.';
          return;
        }
        this.comprobanteError = response.error || 'No fue posible guardar el comprobante.';
      },
      () => {
        this.subiendoComprobante = false;
        this.comprobanteError = 'No fue posible guardar el comprobante.';
      }
    );
  }

  getComprobantePagoUrl(): string {
    const archivo = this.comprobantePago?.archivo || '';
    if (!archivo) {
      return '';
    }
    return archivo.indexOf('http') === 0 ? archivo : environment.BASE_URL + archivo;
  }

  private paymentRoutePrefix(): string {
    return this.router.url.indexOf('/cliente/') === 0 ? '/cliente' : '/perfil';
  }

  onMontoPagoChange(): void {
    this.validarMontoPago();
  }

  private validarMontoPago(): boolean {
    const amount = Number(this.montoPago);
    const min = Number(this.montoMinimo || 300);
    const max = Number(this.montoMaximo || 0);

    if (!amount || Number.isNaN(amount)) {
      this.montoError = 'Ingresa el monto que deseas pagar.';
      return false;
    }

    if (amount < min) {
      this.montoError = `El monto minimo es $${min.toFixed(2)}.`;
      return false;
    }

    if (max > 0 && amount > max) {
      this.montoError = `El monto maximo es $${max.toFixed(2)}.`;
      return false;
    }

    this.montoError = '';
    return true;
  }

  private normalizarMonto(value: any): number {
    let amount = Number(value);
    if (!amount || Number.isNaN(amount)) {
      amount = this.montoMinimo;
    }
    if (amount < this.montoMinimo) {
      amount = this.montoMinimo;
    }
    if (this.montoMaximo > 0 && amount > this.montoMaximo) {
      amount = this.montoMaximo;
    }
    return Math.round(amount * 100) / 100;
  }
}
